import React from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useModal } from "../context/ModalContext";
import useScrollAnimation from "../hooks/useScrollAnimation";
import { DottedSurface } from "./ui/dotted-surface";

const ServiceTrackPage = ({
  trackLabel,
  title,
  accentLine,
  description,
  topPrimaryCtaLabel,
  topSecondaryCtaLabel,
  engagementId,
  outcomesHeading,
  outcomes,
  metricsHeading,
  metrics,
  phases,
  finalEyebrow,
  finalTitleLine,
  finalAccentLine,
  finalDescription,
  finalCtaLabel,
}) => {
  const { openDemoModal } = useModal();
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.12 });

  return (
    <section className="relative py-24 md:py-28 bg-[--bg-primary] min-h-[70vh]">
      <div className="absolute top-0 left-0 right-0 section-divider" />

      <div ref={sectionRef} className="max-w-[1500px] mx-auto px-4 sm:px-6">
        <div className="relative overflow-hidden soft-card rounded-3xl p-8 md:p-10 lg:p-14">
          <DottedSurface className="opacity-40 [mask-image:radial-gradient(circle_at_76%_24%,black_15%,transparent_75%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_20%,var(--accent-glow)_0%,transparent_46%)]" />

          <div className="relative z-10 max-w-4xl">
            <p
              className={`section-eyebrow mb-5 transition-all duration-700 ease-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
            >
              {trackLabel}
            </p>
            <h1
              className={`text-[clamp(2rem,4.2vw,4rem)] font-semibold text-[--text-primary] leading-[1.02] transition-all duration-700 ease-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
              style={{ transitionDelay: "80ms" }}
            >
              {title}
              <br />
              <span className="accent-wash">{accentLine}</span>
            </h1>
            <p
              className={`mt-6 text-lg text-[--text-secondary] max-w-3xl transition-all duration-700 ease-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
              style={{ transitionDelay: "160ms" }}
            >
              {description}
            </p>

            <div
              className={`mt-9 flex flex-wrap gap-4 transition-all duration-700 ease-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
              style={{ transitionDelay: "240ms" }}
            >
              <button
                onClick={openDemoModal}
                className="px-7 py-3.5 text-[11px] font-medium text-[--bg-primary] uppercase tracking-[0.18em] font-mono bg-[--accent] rounded-full hover:bg-[--accent-hover] transition-all flex items-center gap-2 btn-pattern"
              >
                {topPrimaryCtaLabel}
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href={`#${engagementId}`}
                className="px-7 py-3.5 text-[11px] font-medium text-[--text-primary] uppercase tracking-[0.18em] font-mono bg-transparent border border-[--border] rounded-full hover:border-[--border-hover] hover:bg-[--bg-secondary] transition-colors"
              >
                {topSecondaryCtaLabel}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 grid lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-10 items-start">
          <div
            className={`soft-card rounded-2xl p-7 lg:p-8 transition-all duration-700 ease-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: "300ms" }}
          >
            <p className="section-eyebrow mb-5">{outcomesHeading}</p>
            <div className="space-y-4">
              {outcomes.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-[--accent] shrink-0" />
                  <p className="text-[--text-secondary] leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div
            className={`soft-card rounded-2xl p-7 lg:p-8 transition-all duration-700 ease-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: "380ms" }}
          >
            <p className="section-eyebrow mb-5">{metricsHeading}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {metrics.map((metric) => (
                <div key={`${metric.value}-${metric.label}`}>
                  <p className="text-3xl font-semibold text-[--accent] leading-none">{metric.value}</p>
                  <p className="mt-2 text-sm text-[--text-secondary] uppercase tracking-[0.1em]">
                    {metric.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div id={engagementId} className="mt-14">
          <div
            className={`soft-card rounded-2xl p-7 lg:p-10 transition-all duration-700 ease-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: "460ms" }}
          >
            <p className="section-eyebrow mb-6">Engagement Model</p>
            <div className="grid md:grid-cols-2 gap-5">
              {phases.map((phase) => (
                <article
                  key={phase.step}
                  className="rounded-2xl border border-[--border] bg-[--bg-secondary]/45 p-5"
                >
                  <p className="text-[11px] font-mono font-semibold text-[--accent] uppercase tracking-[0.16em]">
                    Phase {phase.step}
                  </p>
                  <h2 className="mt-2 text-xl font-semibold text-[--text-primary]">{phase.title}</h2>
                  <p className="mt-3 text-[--text-secondary] leading-relaxed">{phase.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14">
          <div
            className={`relative overflow-hidden soft-card rounded-3xl p-8 md:p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 transition-all duration-700 ease-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: "540ms" }}
          >
            <DottedSurface className="opacity-45 [mask-image:radial-gradient(circle_at_70%_45%,black_14%,transparent_76%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_28%,var(--accent-glow)_0%,transparent_48%)]" />

            <div className="relative z-10 max-w-3xl">
              <p className="section-eyebrow mb-4">{finalEyebrow}</p>
              <h2 className="text-[clamp(1.9rem,3.8vw,3.4rem)] font-semibold text-[--text-primary] leading-[1.04]">
                {finalTitleLine}
                <br />
                <span className="accent-wash">{finalAccentLine}</span>
              </h2>
              <p className="mt-5 text-[--text-secondary] text-lg leading-relaxed">{finalDescription}</p>
            </div>

            <div className="relative z-10 flex flex-wrap gap-4">
              <button
                onClick={openDemoModal}
                className="px-7 py-3.5 text-[11px] font-medium text-[--bg-primary] uppercase tracking-[0.18em] font-mono bg-[--accent] rounded-full hover:bg-[--accent-hover] transition-all inline-flex items-center gap-2 btn-pattern"
              >
                {finalCtaLabel}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceTrackPage;
